import { AutoComplete, Button, Input, Loader } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isEmojiInput } from '../../../../../common/utils/CommonUtils';
import { AUTOCOMPLETE_FIELD, MODE, PATTERN_FORM_MAXLENGTH, PATTERN_OCCURRENCE, associationEventType } from '../../../../constants';
import { usePatternRegistryForm } from '../../../../hooks';
import { IFieldDefRowItem, IPatternForm } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { fetchServiceEventRequest } from '../../../../store/actions/sagaActions/services/IngressServiceEventSagaActions';
import { clearFormDataDynamic, comparePatternFormData, createObjGenerator, displayCreate, eventInvalidAndTouchedFn, extractViewFormData, formIsEmptyInEdit, generateFdDataState, verifyErrorEvent } from '../../../../utilities/sources/PatternRegistryUtil';
import classes from './PatternRegistryForm.module.scss';
import FieldDefList from './field-definition-list/FieldDefinitionList';
import Occurrences from './occurrences/Occurrences';

const PatternRegistryForm = (props: IPatternForm) => {
  const { isView, urlParam, isEdit, shiftPatternData, setShowPatternReg, patternList } = props;
  const { t: translate } = useTranslation();
  const HELPER_TEXT = {
    patternNameText: translate('fsmHelperTxt.patternNameText'),
    searchText: translate('fsmHelperTxt.searchText'),
  };
  const REGISTRY_ERRORS = {
    duplicate_pattern_registry_name: translate('registryErrors.duplicate_pattern_registry_name'),
    no_results: translate('registryErrors.no_results')
  };
  const PATTERN_REGISTRY_ERRORS = {
    requiredEventName: translate('patternRegErrors.requiredEventName'),
    requiredSourceName: translate('patternRegErrors.requiredSourceName')
  };

  const dispatch = useDispatch<AppDispatch>();
  const { handleInputChange, formData, setFormParamValues } = usePatternRegistryForm();
  const [fdData, setFdData] = useState<IFieldDefRowItem[]>([]);
  const [fieldCondition, setFieldCondition] = useState('');
  const [eventOptions, setEventOptions] = useState<string[]>([]);
  const [clearData, setClearData] = useState<boolean>(false);
  const [inputSourceValue, setInputSourceValue] = useState('');
  const [inputPatternEventValue, setInputPatternEventValue] = useState('');
  const [disableActions, setDisableActions] = useState(false);
  const [eventErrorMsg, setEventErrorMsg] = useState('');
  const [editModeFD, setEditModeFD] = useState(false);
  const [disableCreate, setDisableCreate] = useState(false);
  const [currentEditId, setCurrentEditId] = useState<string | undefined>(undefined);
  const [patternName, setPatternName] = useState('');
  const [duplicateError, setDuplicateError] = useState<string>('');
  const [fdDataDisable, setFDDataDisable] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [eventVersion, setEventVersion] = useState<number | undefined>(undefined);

  const { activeServiceEvent, isLoading: isEventLoading } = useSelector(
    (state) => state.fetchIngressServiceEventReducer
  );

  const eventErrExistAfter = eventErrorMsg && !isEventLoading;
  const eventInvalidAndTouched = eventInvalidAndTouchedFn(formData);
  const hasEventErrorAfterCall = eventErrorMsg.length > 0 && !isEventLoading; // check error while not loading
  const isErrorEvent = verifyErrorEvent(formData, eventErrorMsg, isEventLoading);
  const classNameEvent = isErrorEvent ? classes['cp-pr-autocomplete-error'] : classes['cp-pr-autocomplete-relative'];
  const viewOrEdit = !isView || isEdit;
  const btnText = isEdit ? translate('registry_header.btn_update') : translate('registry_header.btn_save');

  /**
   * @method fieldConditionChangeHandler
   * @description update Field Condition state which is referenced in object for create API
   * @param e
   */
  const fieldConditionChangeHandler = (val: string) => {
    const newFormData = { ...formData };
    newFormData.fieldCondition.value = val;
    setFormParamValues(newFormData);
    setFieldCondition(val);
  };

  /**
    * checks for duplicate names in form and existing list items
    * @returns boolean
    */
  const checkDuplicateName = () => {
    const hasDuplicate = patternList.find((item) => item.patternName === formData.patternRegistryName.value);
    return hasDuplicate === undefined;
  };

  /**
   * @method nameChangeHandler
   * @description Handles the change event for the name input field.
   * Checks if the input value contains emojis and returns early if true.
   * Checks if the input value is a duplicate pattern name and updates the form state accordingly.
   * Updates the pattern name state and triggers the handleInputChange function.
   * @param e - The change event object.
   */
  const nameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmojiInput(e.target.value)) {
      return;
    }
    const duplicateNameValue = patternList.filter((item) => item.patternName === e.target.value);
    if (duplicateNameValue.length > 0) {
      formData.patternRegistryName.isTouched = true;
      formData.patternRegistryName.isValid = false;
      setDuplicateError(String(translate('registryErrors.duplicate_pattern_name')));
      setFormParamValues(formData);
    } else {
      setDuplicateError('');
    }
    handleInputChange(e);
    setPatternName(e.target.value);
  };

  /**
   * @method handleEventSelect
   * @description called when an event is selected from searched event panel
   */
  const handleEventSelect = (value: string) => {
    const newFormData = { ...formData };
    const [, , third] = value.split(' ');
    const selectedEventVersion = third ? parseInt(third.split(')')[0], 10) : 1;
    newFormData.eventName.value = value ?? '';
    newFormData.eventName.isTouched = true;
    newFormData.eventName.version = selectedEventVersion;
    setEventVersion(selectedEventVersion);
    setFormParamValues(newFormData);
    setInputPatternEventValue(value);
    setEventOptions([]);
  };

  /**
   * @method handleEventChange
   * @description triggered while typing in source Autocomplete
   * @param value
   */
  const handleEventChange = (value: string) => {
    const newFormData = { ...formData };
    newFormData.eventName.value = value; // needed for accurate disabling of Create button
    newFormData.eventName.isTouched = true;
    setFormParamValues(newFormData); // needed for accurate disabling of Create button
    setInputPatternEventValue(value);

    if (formData.eventName.value.length > 2) dispatch(fetchServiceEventRequest(value, 'active', associationEventType.INGRESS));
    if (inputPatternEventValue.length < 1) setClearData(false);
  };

  /**
   * @method listUpdater
   * @description handler fn to update field condition list
   * @param listItem
   */
  const listUpdater = (listItem: IFieldDefRowItem, mode: string = MODE.ADD) => {
    let updatedList = [...fdData];
    if (mode === MODE.ADD) {
      updatedList.unshift(listItem);
      const newFormData = { ...formData };
      clearFormDataDynamic(newFormData, [formData.fieldDefName.name, formData.fieldDefOperator.name, formData.fieldDefValue.name, formData.fieldDefContentType.name, formData.fieldDefConversionPattern.name]);
      formData.fieldDefIsRequired.value = false;
    }
    if (mode === MODE.EDIT && currentEditId) {
      updatedList = updatedList.filter((item: IFieldDefRowItem) => item.id !== currentEditId);
      const updatedListItem = {
        ...listItem,
        id: currentEditId // reassign the id chosen for edit
      };
      updatedList.unshift(updatedListItem);
      const newFormData = { ...formData };
      clearFormDataDynamic(newFormData, [formData.fieldDefName.name, formData.fieldDefOperator.name, formData.fieldDefValue.name,
      formData.fieldDefContentType.name, formData.fieldDefConversionPattern.name]);
      formData.fieldDefIsRequired.value = false;
      setCurrentEditId(undefined);
      setEditModeFD(false);
    }
    setPageNumber(0);
    setFdData(updatedList);
    setDisableActions(false);
  };

  /**
   * @method deleteListItem
   * @description function to delete a item in field definition list
   * @param itemId
   */
  const deleteListItem = (itemId: string) => {
    let updatedList = [...fdData];
    updatedList = updatedList.filter((item) => item.id !== itemId);
    setFdData(updatedList);
  };

  /**
   * @method editListItem
   * @description fn to edit a item in field definition list
   * @param itemId
   */
  const editListItem = (itemId: string) => {
    const getItem = fdData.find((item) => item.id === itemId);
    if (getItem) {
      const newFormData = { ...formData };
      newFormData.fieldDefName.value = getItem?.name;
      newFormData.fieldDefOperator.value = getItem?.operator;
      newFormData.fieldDefValue.value = getItem?.value;
      newFormData.fieldDefContentType.value = getItem?.contentType;
      newFormData.fieldDefConversionPattern.value = getItem?.conversionPattern;
      newFormData.fieldDefIsRequired.value = getItem?.required;
      setCurrentEditId(getItem.id);
      setDisableActions(true);
      setEditModeFD(true);
      setFormParamValues(newFormData);
    }
  };

  /**
   * @method localCreateUpdateClickHandler
   * @description creates a final object for pattern registry create API
   */
  const localCreateUpdateClickHandler = () => {
    if (!isEdit && !urlParam.patternname && !urlParam.viewResponse) {
      const requestBody = createObjGenerator(formData, fdData);
      if (eventVersion) {
        requestBody.definition.eventVersion = eventVersion;
      }
      shiftPatternData(requestBody.definition);
      setShowPatternReg(false);
    }
    if (isEdit) {
      const requestBody = createObjGenerator(formData, fdData, urlParam.viewResponse?.eventVersion, isEdit);
      if (eventVersion) {
        requestBody.definition.eventVersion = eventVersion;
      }
      shiftPatternData(requestBody.definition);
      setShowPatternReg(false);
    }
  };

  /**
   * @method enableCancel
   * @description Determines whether the cancel button should be enabled or disabled.
   * The cancel button is enabled if any of the form fields have non-empty values.
   * @returns {boolean} True if the cancel button should be enabled, false otherwise.
   */
  const enableCancel = () => {
    const allFieldsRequired = Object.keys(formData);
    const allNotEmpty = allFieldsRequired.some((i) => formData[i as keyof typeof formData].value !== '');
    return allNotEmpty !== true;
  };

  /**
   * @method description
   * @description Handler function for confirming and canceling the dialogue.
   */
  const onConfirmCancelDialogueHandler = () => {
    setShowPatternReg(false);
  };

  /**
  * when we edit a data on field definition list and then delete it, the list gets updated and  the duplicate error will be cleared.
  */
  useEffect(() => {
    if (patternList.length > 0 && checkDuplicateName()) {
      const newFormData = { ...formData };
      newFormData.patternRegistryName.isValid = true;
      setDuplicateError('');
    }
  }, [patternList]);

  /**
   * this useEffect will check if fdData is non empty and update the disable value
   */
  useEffect(() => {
    if (fdData.length > 0) {
      setFDDataDisable(false);
    } else {
      setFDDataDisable(true);
    }
  }, [fdData]);

  /**
   * reset fieldcondition when fdData i.e. field definitions are 0
   */
  useEffect(() => {
    if (fdData.length < 1 && !isView) {
      const newFormData = { ...formData };
      newFormData.fieldN.value = '';
      newFormData.fieldN.error = '';
      newFormData.fieldN.isValid = true;
      newFormData.fieldN.isTouched = false;
      newFormData.fieldCondition.value = '';
      newFormData.fieldCondition.error = '';
      newFormData.fieldCondition.isValid = true;
      newFormData.fieldCondition.isTouched = false;
      setFormParamValues(newFormData);
      setFieldCondition('');
    }
  }, [fdData, isView]);

  /**
   * when in view or edit mode load the formData with viewResponse
   */
  useEffect(() => {
    if (urlParam.patternname !== '' && urlParam.viewResponse && formIsEmptyInEdit(formData) && fdData.length > 0) {
      const viewFormData = extractViewFormData(urlParam.viewResponse, formData);
      const extractedFdData = generateFdDataState(urlParam.viewResponse.patternDefinition.fieldConditions);
      const updatedFieldDef = [...extractedFdData];
      setFdData(updatedFieldDef);
      setFormParamValues(viewFormData);
      setFieldCondition(viewFormData.fieldCondition.value);
    }
  }, [urlParam, isView, isEdit, formData, fdData]);

  /**
   * when create or update button in header is clicked
   */
  useEffect(() => {
    if (isEdit || isView) {
      if (urlParam?.viewResponse) {
        const viewFormData = extractViewFormData(urlParam?.viewResponse, formData);
        const extractedFdData = generateFdDataState(urlParam.viewResponse?.patternDefinition.fieldConditions);
        const updatedFieldDef = [...extractedFdData];
        setFdData(updatedFieldDef);
        setFormParamValues(viewFormData);
        setFieldCondition(viewFormData.fieldCondition.value);
        setPatternName(viewFormData.patternRegistryName.value);
      }
    }
  }, [isEdit, isView]);
  /**
  * Autocomplete suggestion for event name
  */
  useEffect(() => {
    if (Array.isArray(activeServiceEvent) && activeServiceEvent.length > 0) {
      const filteredData = activeServiceEvent.map((item) => `${item.eventName} (Ver ${item.version})`);
      setEventOptions(filteredData);
    } else if (inputSourceValue.length > 2) {
      setEventOptions([]);
    }
  }, [activeServiceEvent]);

  /**
   * Enable or disable the create/update btn
   */
  useEffect(() => {
    if ((fdData.length < 1 || displayCreate(formData)) && !isEdit && !isView) { // for create mode invalid
      setDisableCreate(true);
    } else if (isEdit && urlParam?.viewResponse) { // for edit mode
      const shouldUpdateEnable = comparePatternFormData(formData, fdData, urlParam.viewResponse);
      setDisableCreate(shouldUpdateEnable);
    } else { // for create mode valid
      setDisableCreate(displayCreate(formData));
    }
    if ((hasEventErrorAfterCall || eventInvalidAndTouched) || inputPatternEventValue.length < 3 || isEventLoading) setDisableCreate(true); // check input length to be more than 2 as api triggers at 3rd character
  }, [fdData, formData, isEdit, urlParam]);

  // set error message when event has no results
  useEffect(() => {
    // adding a inputEventValue length check to prevent useless no result error when switching from view to edit mode
    if (formData.eventName.isTouched && inputPatternEventValue.length > 2 && formData.eventName.value.length > 2 && !activeServiceEvent.length) {
      setEventOptions([]);
      setEventErrorMsg(REGISTRY_ERRORS.no_results);
    } else {
      setEventErrorMsg('');
    }
  }, [eventErrorMsg, formData.eventName.value, activeServiceEvent]);

  // reset the error msg on load of component
  useEffect(() => {
    if (!isView && !isEdit) {
      const newFormData = { ...formData };
      clearFormDataDynamic(newFormData, [formData.patternRegistryName.name, formData.eventName.name, formData.fieldCondition.name, formData.fieldN.name, formData.timeToLive.name, formData.uniqueId.name, formData.fieldDefName.name, formData.fieldDefOperator.name, formData.fieldDefValue.name, formData.fieldDefContentType.name]);
      newFormData.occurrences.value = PATTERN_OCCURRENCE;
      newFormData.occurrences.error = '';
      newFormData.occurrences.isValid = true;
      newFormData.occurrences.isTouched = false;
      newFormData.eventName.value = '';
      newFormData.eventName.isValid = true;
      newFormData.eventName.error = '';
      setFormParamValues(newFormData);
      setInputSourceValue('');
      setInputPatternEventValue('');
      setEventErrorMsg('');
      setFdData([]);
    }
  }, []);

  // display required message for empty eventName field
  useEffect(() => {
    if (!formData.eventName.value && formData.eventName.isTouched) {
      setFormParamValues((prev) => {
        const newFormData = structuredClone(prev);
        newFormData.eventName.error = PATTERN_REGISTRY_ERRORS.requiredEventName;
        newFormData.eventName.isValid = false;
        return newFormData;
      });
    } else {
      setFormParamValues((prev) => {
        const newFormData = structuredClone(prev);
        newFormData.eventName.error = '';
        newFormData.eventName.isValid = true;
        return newFormData;
      });
    }
  }, [formData.eventName.value]);

  /**
   * this useEffect will update the Autocomplet display value
   */
  useEffect(() => {
    if ((urlParam.viewResponse?.eventName && urlParam.viewResponse?.eventVersion) && ((isView) || (isEdit && !formData.eventName.isTouched))) {
      const eventNameValueWithVersion = `${urlParam.viewResponse?.eventName} (Ver ${urlParam.viewResponse.eventVersion})`;
      setInputPatternEventValue(eventNameValueWithVersion);
      setFormParamValues((prev) => {
        const newPatternData = structuredClone(prev);
        newPatternData.eventName.value = eventNameValueWithVersion;
        return newPatternData;
      });
      setEventErrorMsg('');
    }
  }, [isEdit, isView, urlParam.viewResponse?.eventName]); // added formData dependency since we are setting formData

  return (
    <div
      className={classes['registry-form-wrapper']}
      data-testid="cp-pattern-registry-form"
    >
      <Loader show={false} />
      <div className={classes['form-header']}>
        <div className={classes['header-title']}>
          {translate('pattern_registry_form.form_title')}
        </div>
        <div className={classes['header-subtitle']}>
          {translate('pattern_registry_form.form_sub_title')}
        </div>
      </div>
      <Form
        className={classes['form-container']}
        data-testid="cp-pr-form"
      >
        <Form.Group
          className={classes['form-grp']}
        >
          <Input
            type="text"
            placeholder={String(translate('pattern_registry_form.plchd_pattern_name'))}
            label=""
            name="patternRegistryName"
            value={patternName}
            onChange={nameChangeHandler}
            error={!formData.patternRegistryName.isValid || duplicateError !== ''}
            data-testid="pr-name-form-input"
            maxLength={Number(PATTERN_FORM_MAXLENGTH.patternNameLength)}
            disabled={isView || isEdit}
            labelText={String(translate('pattern_registry_form.lbl_pattern_name'))}
            helperText={HELPER_TEXT.patternNameText}
            errorTxt={duplicateError.length > 0 ? duplicateError : formData.patternRegistryName.error}
            isMandatory
          />
        </Form.Group>
        <Form.Group className={classes['form-grp']}>
          {(viewOrEdit) ? (
            <div className={classNameEvent} data-testid="cp-pr-evnt-autocomplete">
              <AutoComplete
                suggestions={eventOptions}
                onSelect={handleEventSelect}
                onqueryUpdate={handleEventChange}
                isLoading={isEventLoading}
                placeHolder={String(translate('pattern_registry_form.plchd_event_name'))}
                clearData={clearData}
                isDisabled={false}
                minSearchLength={3}
                defaultValue={inputPatternEventValue}
                labelText={String(translate('pattern_registry_form.lbl_event_name'))}
                isMandatory
                helperText={HELPER_TEXT.searchText}
                error={hasEventErrorAfterCall || eventInvalidAndTouched}
                errorTxt={eventErrExistAfter ? eventErrorMsg : formData.eventName.error}
                fieldName={AUTOCOMPLETE_FIELD.eventName}
              />
            </div>
          ) : (
            <Input
              label={undefined}
              value={inputPatternEventValue}
              name="eventName"
              data-testid="eventDisabled"
              disabled
              labelText={String(translate('pattern_registry_form.lbl_event_name'))}
              isMandatory
              helperText={HELPER_TEXT.searchText}
            />
          )}
        </Form.Group>
        <Occurrences
          formData={formData}
          handleInputChange={handleInputChange}
          isView={isView}
          isEdit={isEdit}
        />
      </Form>
      <FieldDefList
        formData={formData}
        setFormParamValues={setFormParamValues}
        handleInputChange={handleInputChange}
        deleteHandler={deleteListItem}
        editHandler={editListItem}
        updateFieldCondition={fieldConditionChangeHandler}
        fieldCondition={fieldCondition}
        disabled={disableActions}
        pageNumber={pageNumber}
        updatePageNumber={setPageNumber}
        data={fdData}
        isView={isView}
        isEdit={isEdit}
        updateList={listUpdater}
        fieldEnabler={setDisableActions}
        isEditFD={editModeFD}
        isEditPatternMode={isEdit}
        isViewPatternMode={isView}
        setEditHandler={setEditModeFD}
        setCurrentEditId={setCurrentEditId}
        currentEditId={currentEditId}
      />
      {(viewOrEdit) && (
        <div className={`${classes['pr-btn-contnr']} ci-button-group`}>
          <div>
            <Button
              variant="secondary"
              onClick={onConfirmCancelDialogueHandler}
              disabled={enableCancel()}
              data-testid="cp-pr-form-cancel-btn-btm"
            >
              {translate('registry_header.btn_cancel')}
            </Button>
            <Button
              onClick={localCreateUpdateClickHandler}
              disabled={(disableCreate || fdDataDisable)}
              data-testid="cp-pr-form-create-btn-btm"
            >
              {btnText}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatternRegistryForm;
